/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { authApi } from '@/features/auth/api/auth';
import type { UserProfile, AuthContextType } from './types';

export type { UserProfile };

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Global Authentication Context Provider for AWS Cognito & Django Session Management.
 *
 * Probes the backend BFF `/api/auth/cognito/session/` endpoint on mount to verify
 * authentication status and populate the user profile state without triggering 401 refresh loops.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;

    // Probe the Cognito session endpoint on initial mount
    authApi.getSessionCognito()
      .then((data) => {
        if (isMounted) {
          if (data && data.isAuthenticated) {
            setIsAuthenticated(true);
            setUser(data.user || null);
          } else {
            setIsAuthenticated(false);
            setUser(null);
          }
        }
      })
      .catch(() => {
        if (isMounted) {
          setIsAuthenticated(false);
          setUser(null);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    // Global listener for unauthorized (401) events from HTTP interceptor
    const handleUnauthorized = () => {
      if (isMounted) {
        setIsAuthenticated(false);
        setUser(null);
      }
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => {
      isMounted = false;
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logoutCognito();
    } catch {
      // Ignore logout API failures, ensure client state is cleared
    } finally {
      setIsAuthenticated(false);
      setUser(null);
    }
  }, []);

  // Memoize context value to optimize re-renders across the component tree (Vercel Best Practice)
  const contextValue = useMemo(() => ({
    isAuthenticated,
    isLoading,
    user,
    setAuthenticated: setIsAuthenticated,
    setUser,
    logout,
  }), [isAuthenticated, isLoading, user, logout]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
